import { createSelector, createSlice } from "@reduxjs/toolkit";
import { ensureInteger } from "../../common/utils/generalUtils";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { createMapSelector, dataHookWithCredentialsFactory, DefaultFailureAction, DefaultSuccessAction, initMapFactory, initMultiMapFactory, queryMapFactory } from "../../common/reducers/utilities";
import { dateRangeOverlap, maxDate, minDate, nowISOString } from "../../common/utils/dateUtils";
import { logout } from "../../common/reducers/initContext";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import moment from "moment";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";
import { getDisplayActualTimes, prepareRosterArray } from "./rosterSliceUtils";


const key = roster => ensureInteger(roster.rosterid);

const updateState = (state, roster) => {
    state.data[key(roster)] = roster;
}
const removeState = (state, rosterid) => {
    delete state.data[rosterid];
}

export const rosterStaffSlice = createSlice({
    name: 'staffRosters',
    initialState: {
        data: {},
    },
    reducers: {
        set: (state, action) => {
            const toProcess = new Set(action.payload.ids.map(id => ensureInteger(id)));
            prepareRosterArray(action.payload.data).forEach(roster => {
                updateState(state, roster);
                toProcess.delete(key(roster));
            })
            toProcess.forEach(id => delete state.data[id]);
        },
        setForGroups: (state, action) => {
            const groupids = new Set(action.payload.groupids);
            const toRemove = new Set(Object.values(state.data).filter(r => groupids.has(r.groupid)).map(r => key(r)));
            prepareRosterArray(action.payload.data).forEach(roster => {
                updateState(state, roster);
                toRemove.delete(key(roster));
            });
            toRemove.forEach(rosterid => removeState(state, rosterid));
        },
        setForShifts: (state, action) => {
            const shiftids = new Set(action.payload.shiftids);
            const toRemove = new Set(Object.values(state.data).filter(r => shiftids.has(r.shiftid)).map(r => key(r)));
            prepareRosterArray(action.payload.data).forEach(roster => {
                updateState(state, roster);
                toRemove.delete(key(roster));
            });
            toRemove.forEach(rosterid => removeState(state, rosterid));
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(r => {
                prepareRosterArray(action.payload).forEach(r => state.data[key(r)] = r);
            });
        },
        updateShift: (state, action) => {
            ensureArray(action.payload).forEach(shift => {
                for (const roster of Object.values(state.data)) {
                    if (roster.shiftid === shift.id) {
                        const { display_actual_start, display_actual_end } = getDisplayActualTimes(roster);
                        
                        roster.shift_start = shift.start;
                        roster.shift_end = shift.end;
                        roster.shift_travel = shift.travel;
                        roster.display_actual_start = display_actual_start;
                        roster.display_actual_end = display_actual_end;
                    }
                }
            });
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(roster => delete state.data[key(roster)])
        },
        extraReducers: builder => {
            builder.addCase(logout, () => rosterStaffSlice.getInitialState());
        }
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'roster_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'roster_one');

export const rosterSignup = (network, roster) => network.post('/api/v1/roster', roster, visualizeSuccess('create', rosterStaffSlice.actions.addOrUpdate), visualizeFailure('create'));
export const requestRosterRemove = (network, id) => network.put('/api/v1/roster', { rosterid: id, removal_request: true }, visualizeSuccess('update', rosterStaffSlice.actions.addOrUpdate), visualizeFailure('update'));
export const rosterAccept = (network, id, password) => network.put('/api/v1/roster', { rosterid: id, accepted: nowISOString(), password }, visualizeSuccess('update', rosterStaffSlice.actions.addOrUpdate), visualizeFailure('update'));
export const rosterReject = (network, id) => network.put('/api/v1/roster', { rosterid: id, rejected: nowISOString() }, visualizeSuccess('update', rosterStaffSlice.actions.addOrUpdate), visualizeFailure('update'));

export const clockIntoRoster = (network, data) => network.post('/api/v1/clockIn', data);
export const clockOutOfRoster = (network, data) => network.post('/api/v1/clockOut', data);

const fetchRoster = aggregatingFetch(
    id => network => network.get('/api/v2/staffRoster/' + encodeURIComponent(id), data => rosterStaffSlice.actions.set({ ids: [id], data }), visualizeFailure('get')),
    ids => network => network.post('/api/v2/staffRosters', ids, data => rosterStaffSlice.actions.set({ ids, data }), visualizeFailure('get')),
    { primitiveKey: true }
)
const fetchGroupRoster = aggregatingFetch(
    groupid => network => network.get('/api/v2/staffRostersForGroup/' + encodeURIComponent(groupid), data => rosterStaffSlice.actions.setForGroups({ groupids: [groupid], data }), visualizeFailure('get')),
    groupids => network => network.post('/api/v2/staffRostersForGroups', groupids, data => rosterStaffSlice.actions.setForGroups({ groupids, data }), visualizeFailure('get')),
    { primitiveKey: true }
)

const fetchShiftRosters = aggregatingFetch(
    shiftid => network => network.get('/api/v2/staffRostersForShift/' + encodeURIComponent(shiftid), data => rosterStaffSlice.actions.setForShifts({ shiftids: [shiftid], data }), visualizeFailure('get')),
    shiftids => network => network.post('/api/v2/staffRostersForShifts', shiftids, data => rosterStaffSlice.actions.setForShifts({ shiftids, data }), visualizeFailure('get')),
    { primitiveKey: true }
)

const fetchMemberRostersInRange = (memberid, start, end) => network => network.get(`/api/v2/staffRosters/member/${encodeURIComponent(memberid)}/${encodeURIComponent(start)}/${encodeURIComponent(end)}`, rosterStaffSlice.actions.addOrUpdate, visualizeFailure('get'));

const fetchRostersInRange = aggregatingFetch(
    (start, end) => network => {
        return network.get(`/api/v2/staffRosters/${encodeURIComponent(start)}/${encodeURIComponent(end)}`, rosterStaffSlice.actions.addOrUpdate, visualizeFailure('get'))
    },
    keys => network => {
        return network.get(`/api/v2/staffRosters/${encodeURIComponent(minDate(keys, 0))}/${encodeURIComponent(maxDate(keys, 1))}`, rosterStaffSlice.actions.addOrUpdate, visualizeFailure('get'))
    }
)

export const fetchUnconfirmedRostersInRange = (start, end, config) => network => network.post('/api/v2/staffRosterUnconfirmed', {start, end}, rosterStaffSlice.actions.addOrUpdate, visualizeFailure('get'))

const fetchRostersAwaitingAcceptance = memberid => network => network.get('/api/v2/staffRostersAwaitingConfirmation', rosterStaffSlice.actions.addOrUpdate, visualizeFailure('get'))

// #endregion
const selectRawRosters = state => state.staffRosters.data;
const selectDataByShift = createMapSelector(
    [selectRawRosters],
    (rosters) => {
        return Object.values(rosters).reduce((res, r) => {
            if (!res[r.shiftid]) {
                res[r.shiftid] = [];
            }
            res[r.shiftid].push(r);
            return res;
        }, {});
    }
);
const selectDataByGroup = createMapSelector(
    [selectRawRosters],
    (rosters) => {
        return Object.values(rosters).reduce((res, r) => {
            if (!res[r.groupid]) {
                res[r.groupid] = [];
            }
            res[r.groupid].push(r);
            return res;
        }, {});
    }
);

const selectShiftRosters = shiftidsInput => {
    const shiftids = ensureArray(shiftidsInput);
    return createSelector([selectDataByShift], shiftRosters => shiftids.flatMap(shiftid => shiftRosters[shiftid] ?? []));
 }

const selectGroupRosters = groupids => createSelector([selectDataByGroup], groupidsInput => {
    const groupids = ensureArray(groupidsInput);
    return createSelector([selectDataByGroup], groupRosters => groupids.flatMap(groupid => groupRosters[groupid] ?? []));
});

const selectMemberRostersInRange = (memberid, start, end) => createSelector([selectRawRosters], rosters => Object.values(rosters)?.filter(r => r.memberid === memberid && dateRangeOverlap({ start: r.start ?? r.shift_start, end: r.end ?? r.shift_end }, { start, end })) ?? [])
const selectRostersInRange = (start, end) => createSelector([selectRawRosters], rosters => Object.values(rosters)?.filter(r => dateRangeOverlap({ start: r.start ?? r.shift_start, end: r.end ?? r.shift_end }, { start, end })) ?? []);

const selectPotentiallyUnacceptedRosters = (memberid) => createSelector([selectRawRosters], rosters => {
    return Object.values(rosters)?.filter(r => r.memberid === memberid && !r.accepted && !r.rejected && r.offered && !r.hours_confirmed) ?? [];
})
// #region selectors

// #endregion

const listen = {
    rosterStaffCreated: (roster, { dispatch, websocket }) => {
        markFetched(websocket, 'roster', roster);
        dispatch(rosterStaffSlice.actions.addOrUpdate(roster))
    },
    rosterStaffUpdated: (roster, { dispatch }) => dispatch(rosterStaffSlice.actions.addOrUpdate(roster)),
    rosterStaffRemoved: (roster, { dispatch }) => dispatch(rosterStaffSlice.actions.remove(roster)),
    shiftStaffUpdated: (shift, { dispatch }) => dispatch(rosterStaffSlice.actions.updateShift(shift))
}

export const memberRequiresRosterConfirmation = (member, shift, roster, memberRequiresRosterConfirmation) => memberRequiresRosterConfirmation(moment, { ...(JSON.parse(JSON.stringify(member))), shift_customfields: shift?.customfields ?? {}, ...roster })

const options = {
    cleanup: { root: rosterStaffSlice.name, all: state => Object.values(selectRawRosters(state)), removeAll: rosterStaffSlice.actions.remove, key },
    condition: credentials => credentials?.hasRole('member')
}
const rosterInitByID = initMapFactory(fetchRoster, listen, { name: 'staffRosterByID' });
registerEntityBasedFetch('roster', rosterInitByID);

const rosterInitByShift = queryMapFactory(fetchShiftRosters, 'rosterid', rosterInitByID, listen);
registerEntityBasedFetch('shift', rosterInitByShift);

export const [useStaffRostersForShift] = dataHookWithCredentialsFactory(selectShiftRosters, rosterInitByShift, options);
export const [useStaffRostersForShifts] = dataHookWithCredentialsFactory(selectShiftRosters, initMultiMapFactory(rosterInitByShift, listen), options);

const rosterInitByGroup = queryMapFactory(fetchGroupRoster, 'rosterid', rosterInitByID, listen, { name: 'staffRostersForGroup' });
registerEntityBasedFetch('group', rosterInitByGroup);

export const [useStaffRostersForGroup] = dataHookWithCredentialsFactory(selectGroupRosters, rosterInitByGroup, options);
export const [useStaffRostersForGroups] = dataHookWithCredentialsFactory(selectGroupRosters, initMultiMapFactory(rosterInitByGroup, listen), options);
export const [useStaffMemberRostersInRange] = dataHookWithCredentialsFactory(selectMemberRostersInRange, queryMapFactory(fetchMemberRostersInRange, 'rosterid', rosterInitByID, listen), options);
export const [useStaffRostersInRange] = dataHookWithCredentialsFactory(selectRostersInRange, queryMapFactory(fetchRostersInRange, 'rosterid', rosterInitByID, listen), options);
export const [useStaffPotentiallyUnacceptedRosters] = dataHookWithCredentialsFactory(selectPotentiallyUnacceptedRosters, queryMapFactory(fetchRostersAwaitingAcceptance, 'rosterid', rosterInitByID, listen ), options)

const reducer = { name: rosterStaffSlice.name, reducer: rosterStaffSlice.reducer }
export default reducer;